import * as THREE from "three";
import type { AssetPipeline } from "../core/assets";
import { TERRAIN_MATERIALS as MAT } from "./terrain-materials";
import { addCrateCluster, addFenceLoop, addRoofUnits, addSmallShed, addUtilityPole } from "./props";
import { addCityDistrict } from "./city";

type Rng = { next: () => number };
type HeightFn = (x: number, z: number) => number;

function rr(rng: Rng, lo: number, hi: number): number {
  return lo + rng.next() * (hi - lo);
}

function addMesh(group: THREE.Group, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number, rotationY = 0): void {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, y, z);
  mesh.rotation.y = rotationY;
  group.add(mesh);
}

function addUrbanTower(group: THREE.Group, rng: Rng, x: number, z: number, getHeight: HeightFn): void {
  const width = rr(rng, 10, 22);
  const depth = rr(rng, 9, 20);
  const height = rr(rng, 12, 46);
  const baseY = getHeight(x, z);
  const rotation = rr(rng, -0.12, 0.12);

  addMesh(group, new THREE.BoxGeometry(width, height, depth), MAT.urban, x, baseY + height / 2, z, rotation);

  const roofHeight = 0.9;
  addMesh(group, new THREE.BoxGeometry(width + 0.8, roofHeight, depth + 0.8), rng.next() > 0.42 ? MAT.roofMetal : MAT.roof, x, baseY + height + roofHeight / 2, z, rotation);
  addRoofUnits(group, x, baseY + height + roofHeight, z, width, depth, rotation);

  if (height > 18) {
    const capW = width * rr(rng, 0.32, 0.58);
    const capD = depth * rr(rng, 0.32, 0.58);
    const capH = rr(rng, 2, 7);
    addMesh(group, new THREE.BoxGeometry(capW, capH, capD), MAT.urban, x + rr(rng, -1.8, 1.8), baseY + height + capH / 2, z + rr(rng, -1.8, 1.8), rotation);
  }

  if (rng.next() > 0.6) {
    const annexW = width * rr(rng, 0.28, 0.48);
    const annexD = depth * rr(rng, 0.28, 0.44);
    const annexH = height * rr(rng, 0.35, 0.6);
    addMesh(group, new THREE.BoxGeometry(annexW, annexH, annexD), MAT.urban, x + rr(rng, -width * 0.18, width * 0.18), baseY + annexH / 2, z + rr(rng, -depth * 0.18, depth * 0.18), rotation);
  }
}

function addSuburbanHouse(group: THREE.Group, rng: Rng, x: number, z: number, getHeight: HeightFn): void {
  const width = rr(rng, 8, 16);
  const depth = rr(rng, 7, 13);
  const height = rr(rng, 3.8, 7.4);
  const baseY = getHeight(x, z);
  const rotation = rr(rng, 0, Math.PI * 2);

  addMesh(group, new THREE.BoxGeometry(width, height, depth), MAT.suburban, x, baseY + height / 2, z, rotation);

  const roofHeight = height * rr(rng, 0.28, 0.46);
  const roofGeom = new THREE.CylinderGeometry(0, width * 0.58, roofHeight, 4, 1);
  roofGeom.rotateY(Math.PI / 4);
  addMesh(group, roofGeom, MAT.roof, x, baseY + height + roofHeight / 2, z, rotation);
  addFenceLoop(group, x, z, width + 5.2, depth + 5.2, rotation, getHeight);

  if (rng.next() > 0.45) {
    const annexW = width * rr(rng, 0.26, 0.42);
    const annexD = depth * rr(rng, 0.32, 0.46);
    const annexH = height * rr(rng, 0.6, 0.9);
    addMesh(group, new THREE.BoxGeometry(annexW, annexH, annexD), MAT.suburban, x + Math.cos(rotation) * (width * 0.22), baseY + annexH / 2, z + Math.sin(rotation) * (width * 0.22), rotation);
  }

  if (rng.next() > 0.55) {
    const porchW = width * rr(rng, 0.22, 0.34);
    const porchD = depth * rr(rng, 0.18, 0.28);
    addMesh(group, new THREE.BoxGeometry(porchW, 0.25, porchD), MAT.compound, x + Math.cos(rotation) * (depth * 0.42), baseY + 0.16, z + Math.sin(rotation) * (depth * 0.42), rotation);
  }

  if (rng.next() > 0.35) {
    const poleX = x - Math.sin(rotation) * (width * 0.7);
    const poleZ = z + Math.cos(rotation) * (width * 0.7);
    addUtilityPole(group, poleX, poleZ, rotation, getHeight);
  }
}

function addRuralCompound(group: THREE.Group, rng: Rng, x: number, z: number, size: number, getHeight: HeightFn): void {
  const wallH = 2.2;
  const half = size * 0.5;
  const segments: Array<[number, number, number, number, number, number]> = [
    [x, z - half, size, wallH, 1.4, 0],
    [x, z + half, size, wallH, 1.4, 0],
    [x - half, z, 1.4, wallH, size, 0],
    [x + half, z, 1.4, wallH, size, 0],
  ];

  for (const [sx, sz, w, h, d] of segments) {
    const y = getHeight(sx, sz);
    addMesh(group, new THREE.BoxGeometry(w, h, d), MAT.compound, sx, y + h / 2, sz);
  }

  const gateWidth = rr(rng, 5, 8);
  const gateY = getHeight(x, z - half);
  addMesh(group, new THREE.BoxGeometry(gateWidth, wallH + 0.4, 0.6), MAT.roofMetal, x, gateY + (wallH + 0.4) / 2, z - half);

  const buildingCount = Math.floor(rr(rng, 1, 4));
  for (let i = 0; i < buildingCount; i++) {
    const bw = rr(rng, 5, 13);
    const bd = rr(rng, 4, 10);
    const bh = rr(rng, 3, 6);
    const bx = x + rr(rng, -size * 0.24, size * 0.24);
    const bz = z + rr(rng, -size * 0.24, size * 0.24);
    const by = getHeight(bx, bz);
    addMesh(group, new THREE.BoxGeometry(bw, bh, bd), MAT.rural, bx, by + bh / 2, bz);

    const roof = new THREE.Mesh(new THREE.BoxGeometry(bw + 0.5, 0.3, bd + 0.5), MAT.roof);
    roof.position.set(bx, by + bh + 0.15, bz);
    group.add(roof);
  }

  addCrateCluster(group, x + rr(rng, -size * 0.16, size * 0.16), z + rr(rng, -size * 0.16, size * 0.16), getHeight);

  if (rng.next() > 0.45) {
    addSmallShed(group, x - size * 0.22, z + size * 0.18, rr(rng, 0, Math.PI * 2), getHeight);
  }

  if (rng.next() > 0.52) {
    const towerY = getHeight(x + half * 0.34, z + half * 0.34);
    addMesh(group, new THREE.BoxGeometry(1.2, 5.8, 1.2), MAT.compound, x + half * 0.34, towerY + 2.9, z + half * 0.34);
  }
}

function addPrimitiveUrbanGrid(group: THREE.Group, rng: Rng, getHeight: HeightFn): void {
  const gridW = 5;
  const gridD = 5;
  const blockSpan = 52;
  const halfX = (gridW * blockSpan) / 2;
  const halfZ = (gridD * blockSpan) / 2;

  for (let gx = 0; gx < gridW; gx++) {
    for (let gz = 0; gz < gridD; gz++) {
      const cx = -halfX + gx * blockSpan + blockSpan / 2;
      const cz = -halfZ + gz * blockSpan + blockSpan / 2;
      const count = Math.floor(rr(rng, 2, 6));
      for (let i = 0; i < count; i++) {
        addUrbanTower(group, rng, cx + rr(rng, -20, 20), cz + rr(rng, -20, 20), getHeight);
      }
    }
  }
}

export function addSettlementStructures(group: THREE.Group, rng: Rng, size: number, getHeight: HeightFn, assets?: AssetPipeline): void {
  // The urban core is asset-backed (street grid + city-kit blocks); the
  // primitive tower grid remains only as the no-assets/load-failure fallback.
  if (assets) {
    void addCityDistrict(group, assets, getHeight).catch((error) => {
      console.warn("Falling back to primitive settlement districts", error);
      addPrimitiveUrbanGrid(group, rng, getHeight);
    });
  } else {
    addPrimitiveUrbanGrid(group, rng, getHeight);
  }

  const innerR = 175;
  const outerR = size / 2 - 130;
  for (let i = 0; i < 90; i++) {
    const angle = rng.next() * Math.PI * 2;
    const radius = rr(rng, innerR, outerR);
    addSuburbanHouse(group, rng, Math.cos(angle) * radius, Math.sin(angle) * radius, getHeight);
  }

  const half = size / 2 - 80;
  for (let i = 0; i < 14; i++) {
    const x = rr(rng, -half, half);
    const z = rr(rng, -half, half);
    if (Math.hypot(x, z) < 240) continue;
    addRuralCompound(group, rng, x, z, rr(rng, 22, 48), getHeight);
  }
}
